/**
 * @ngdoc directive
 * @name flowHistoryStep
 * @module flowingly.runner.flow
 * @description This component is used to display information about a completed step.
 * @usage
 * ```
     <flow-history-step ng-if="step.completed" step="step" is-mobile="$ctrl.isMobile"></flow-history-step>
 * ``` 
 * ### Notes
 * See Also: https://bizflo.atlassian.net/wiki/display/TECH/Angular+Flow+Components
 * ### Properties
 * #### Inputs
 * * step: the step data to display (JSON)
 * * isMobile: show mobile view if set
 */

import { SharedAngular } from '@Client/@types/sharedAngular';
import ActivityTaskType from '@Shared.Angular/@types/core/contracts/queryModel/workflow/steps/activityTaskType';
import { FormFieldType } from '@Shared.Angular/flowingly.services/flowingly.constants';
import angular, {
  IHttpService,
  ISCEService,
  ITimeoutService,
  IWindowService
} from 'angular';

const AvatarNames = {
  Email: 'Email',
  ExternalUser: 'External User'
} as const;

angular.module('flowingly.runner.flow').component('flowHistoryStep', {
  bindings: {
    step: '<',
    isMobile: '<', //show mobile view if set
    flow: '<'
  },
  controller: [
    '$sce',
    '$http',
    'sessionService',
    '$timeout',
    '$window',
    'pubsubService',
    'fileService',
    'browserUtilsService',
    'momentService',
    function (
      $sce: ISCEService,
      $http: IHttpService,
      sessionService: SharedAngular.SessionService,
      $timeout: ITimeoutService,
      $window: IWindowService,
      pubsubService: SharedAngular.PubSubService,
      fileService: SharedAngular.FileService,
      browserUtilsService: SharedAngular.BrowserUtilsService,
      moment: Moment
    ) {
      const $ctrl = this;
      $ctrl.$sce = $sce;
      $ctrl.isMobileApp = browserUtilsService.isCordovaApp();
      $ctrl.completedApprovals = $ctrl.step.completedApprovals;
      $ctrl.stepStatus = getStepStatus();
      $ctrl.numberOfCompletedApprovals = $ctrl.completedApprovals.length;
      $ctrl.activityTaskType = ActivityTaskType;
      $ctrl.stepWebhooks = undefined;
      $ctrl.stepTasks = undefined;
      $ctrl.createdDateFormatted = moment($ctrl.step.createdDate).format(
        'dddd, MMMM D, YYYY'
      );

      const subscriberId = 'flowHistoryStep';
      $ctrl.displayStepWebhooks = () => {
        return $ctrl.stepWebhooks != null && $ctrl.stepWebhooks.length > 0;
      };

      $ctrl.displayStepTasks = () => {
        return $ctrl.stepTasks != null && $ctrl.stepTasks.length > 0;
      };

      $ctrl.$onInit = function () {
        $timeout(() => {
          fileService.addFileDownloadClickEventListener();
        }, 500); // Adding execution delay avoids clashing with the instruction field calling the same
        // function and in adding duplicate event listeners.

        pubsubService.subscribe(
          'SIGNALR_RUNNER_STEP_WEBHOOK_UPDATED',
          (event, data: string) => {
            // Update the status of the webhook. E.g Pending to Success.
            if (data != null && $ctrl.stepWebhooks != null) {
              const json = JSON.parse(data);
              if (
                json != null &&
                json.stepId != null &&
                json.webhookId != null
              ) {
                for (let i = 0; i < $ctrl.stepWebhooks.length; i++) {
                  const hook = $ctrl.stepWebhooks[i];
                  if (hook && hook.id === json.webhookId) {
                    $ctrl.stepWebhooks[i] = {
                      status: json.status,
                      id: json.webhookId,
                      name: hook.name
                    };
                    break;
                  }
                }
              }
            }
          },
          subscriberId
        );

        $ctrl.$onDestroy = function () {
          pubsubService.unsubscribeAll(subscriberId);
        };

        $ctrl.stepWebhooks = $ctrl.step.webhooks;

        // Sort the step tasks by their completed date.
        const tasks = $ctrl.step.stepTasks;
        if (tasks != null) {
          tasks.sort(function (a, b) {
            return (
              (new Date(b.completedDateUtc) as any) -
              (new Date(a.completedDateUtc) as any)
            );
          });
          $ctrl.stepTasks = tasks;
        }

        $ctrl.hasEmailRecipients = function () {
          const recipients =
            $ctrl.step.nodeCustomMail?.selectedEmailRecipients || [];
          return recipients.some(
            (r) => r.displayName && r.displayName.trim() !== ''
          );
        };
        $ctrl.getAvatarName = function () {
          switch ($ctrl.step.stepType) {
            case $ctrl.activityTaskType.CUSTOM_EMAIL:
              return AvatarNames.Email;
            case $ctrl.activityTaskType.EXTERNAL_USER_TASK:
              return AvatarNames.ExternalUser;
            default:
              return $ctrl.step.completedByName;
          }
        };

        $ctrl.getStepStatusText = function () {
          if ($ctrl.isExternalUserStep() && !$ctrl.step.completedDateMoment) {
            if (!$ctrl.hasEmailRecipients()) {
              return 'Waiting for email to be delivered to External User';
            }
            return 'Waiting for input by External User';
          }
          return `${$ctrl.stepStatus} ${$ctrl.step.completedDateMoment} by ${$ctrl.step.whoCompletedStep}`;
        };
      };

      $ctrl.shouldShowExternalUserEmailSection = function () {
        if (!$ctrl.isExternalUserStep()) {
          return false;
        }

        if ($ctrl.step.nodeCustomMail !== null) {
          return true;
        }
        return !$ctrl.step.completedDateMoment;
      };

      $ctrl.shouldShowStepFields = function () {
        if (!$ctrl.isExternalUserStep()) {
          return true;
        }

        return !!$ctrl.step.completedDateMoment;
      };

      $ctrl.isExternalUserStep = function () {
        return (
          $ctrl.step.stepType === $ctrl.activityTaskType.EXTERNAL_USER_TASK
        );
      };

      $ctrl.showResendEmailModal = false;
      $ctrl.openResendEmailDialog = function () {
        $ctrl.showResendEmailModal = true;
      };

      $ctrl.closeResendEmailModal = function (result?: {
        selectedEmailRecipients: unknown[];
      }) {
        if (result && result.selectedEmailRecipients) {
          $ctrl.step.nodeCustomMail.selectedEmailRecipients =
            result.selectedEmailRecipients;
        }
        $ctrl.showResendEmailModal = false;
      };

      $ctrl.useFlowHistoryFieldValueDirective = (type) => {
        if (
          type !== FormFieldType.FILE_UPLOAD &&
          type !== FormFieldType.TABLE &&
          type !== FormFieldType.INSTRUCTION &&
          type !== FormFieldType.MULTISELECT_LIST &&
          type !== FormFieldType.RADIO_BUTTON_LIST &&
          type !== FormFieldType.TASK_LIST &&
          type !== FormFieldType.CHECKBOX &&
          type !== FormFieldType.SIGNATURE &&
          type !== FormFieldType.ATTACH_DOCUMENT &&
          type !== FormFieldType.IMAGE &&
          type !== FormFieldType.ADDRESS &&
          type !== FormFieldType.EXTERNAL_BUSINESS &&
          
          // TECH DEBT: flows.formatter.ts converts backend FormFieldTypeEnum to mismatched strings
          // (e.g., 'textArea' not 'textarea', 'approvecomment' not 'approvalcomment').
          // These hardcoded values match the actual runtime output. See flows.formatter.ts:426-500
          type !== 'textArea' &&
          type !== 'dropdown' &&
          type !== 'approvecomment'
        ) {
          return true;
        } else {
          return false;
        }
      };

      function getStepStatus() {
        switch ($ctrl.step.status) {
          case 0:
            return 'ToDo';
          case 1:
            return 'Hold';
          case 2:
            return 'Completed';
          case 3:
            return 'Abandoned';
          default:
        }
      }

      $ctrl.isTableHasFileUpload = function (x) {
        if (x.indexOf('"type":4') > -1) return true;
      };

      /*When integrating webhooks from Power Automate, they are added to the existing 
      node rather than being published as a new flow model, as is the case when adding webhooks through Flowingly. 
      This condition is implemented to prevent misleading webhook status messages for webhooks added from Power Automate.*/
      $ctrl.shouldShowWebhookStatus = function () {
        const powerAutomateWebhooksWithNullStatus = $ctrl.stepWebhooks.filter(
          function (hook) {
            return hook.type === 1 && hook.status === null;
          }
        );
        return !(
          powerAutomateWebhooksWithNullStatus.length ===
          $ctrl.stepWebhooks.length
        );
      };

      $ctrl.isWebhookQueued = function (hook) {
        if (hook.type === 0 && hook.status == null) {
          return true;
        }
        return false;
      };

      $ctrl.isWebhookFailed = function (hook) {
        if (hook.status === 1 || hook.status === 2 || hook.status === 3) {
          return true;
        }
        return false;
      };
    }
  ],
  templateUrl:
    'Client/runner.flow/runner.flow.history/runner.flow.history.step.component.tmpl.html'
});
